import {
  BillingQueryDocument,
  MeDocument,
  PlanPriceType,
  PlanType,
  PlatformType,
  useBillingQueryQuery,
  useCompleteOnboardingMutation,
  useCreateSubscriptionMutation,
  usePlansQueryQuery,
  UserPermission,
  useTrackEventMutation,
  type BillingQueryQuery,
  type MeQuery,
  type PlanPriceItem,
} from '@/shared/graphql/legacy/generated'
import { createCardElement, getStripe } from '@/shared/services/stripe'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { formatPrice } from '@/shared/utils/money'
import { analytics } from '@/shared/services/analytics'
import type { Stripe, StripeCardElement } from '@stripe/stripe-js'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

export function useCheckout() {
  const { result } = usePlansQueryQuery()
  const { mutate: createSubscription } = useCreateSubscriptionMutation()
  const { refetch } = useBillingQueryQuery()
  const { mutate: trackEvent } = useTrackEventMutation()
  const { mutate: completeOnboarding } = useCompleteOnboardingMutation()

  const route = useRoute()
  const router = useRouter()
  const dialog = useDialog()
  const billingStore = useBillingStore()
  const userStore = useUserStore()
  const webviewStore = useWebviewStore()

  const stripe = ref<Stripe | null>(null)
  const card = ref<StripeCardElement | null>(null)
  const acceptTerms = ref(false)
  const loading = ref(false)
  const errorMessage = ref('')

  const selectedPlan = computed(() => billingStore.selectedPlan)
  const selectedPlanItem = computed(() => billingStore.selectedPlanItem)
  const selectedBillingCycle = computed(() => billingStore.selectedBillingCycle)

  const getBillingCyclePrice = (price: PlanPriceItem) => {
    return formatPrice({
      amount: (price.details?.amount || 0) / 100,
      currency: price.details?.currency,
    })
  }

  const hasMoreThanOnePrice = computed(
    () => (selectedPlanItem.value?.prices.length || 0) > 1
  )

  const cardClasses = computed(() => ({
    'pointer-events-none opacity-50': loading.value,
  }))

  const paymentMethod = computed(
    () => billingStore.billing?.paymentMethod?.card
  )

  const setupStripe = async () => {
    stripe.value = await getStripe()
    if (!stripe.value) return
    card.value = createCardElement(stripe.value)
    card.value.on('change', () => {
      errorMessage.value = ''
    })
    card.value?.mount('#card')
  }

  onMounted(async () => {
    if (!paymentMethod.value?.last4) {
      await setupStripe()
    }

    const subscriptionPrice = selectedPlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )
    const fullAmount = subscriptionPrice?.details?.amount || 0
    const amount = fullAmount / 100

    analytics.track('checkout_initiated', {
      planName: selectedPlan.value?.name,
      currency: subscriptionPrice?.details?.currency,
      amount,
    })

    trackEvent({
      name: 'initiate_checkout',
      data: JSON.stringify({
        id: selectedPlanItem.value?.productId,
        data: {
          PLAN: selectedPlan.value?.name,
          AMOUNT: fullAmount,
        },
      }),
    })
  })

  const handleChangeClick = () => {
    if (route.path.includes('register'))
      return router.push('/register/select-plan')
    if (route.path.includes('onboarding')) return router.push('/onboarding/pro')
    if (route.path.includes('upgrade')) return router.push('/upgrade')
    if (route.path.includes('change')) return router.push('/change-plans')
  }

  const formatUpsellText = (text?: string) => {
    if (!text) return
    return text.replace(
      '<price>',
      selectedPlanItem.value?.upsell?.planItem.prices?.[0]?.details
        ?.renderedPrice || ''
    )
  }

  const handleUpdatePaymentMethod = () => {
    dialog.open(Dialogs.UpdatePaymentMethod)
  }

  const handleUpgradeClick = () => {
    if (
      !selectedPlanItem.value?.upsell?.plan ||
      !selectedPlanItem.value?.upsell.planItem
    )
      return

    analytics.track('upsell_cta_clicked', {
      amount: (selectedPlanItem.value.prices?.[0]?.details?.amount || 0) / 100,
      currency: selectedPlanItem.value.prices?.[0]?.details?.currency,
    })

    const billingCycle =
      selectedPlanItem.value.prices[0].details?.billingCycleType || 'month'

    billingStore.setPlanData({
      plan: selectedPlanItem.value.upsell.plan,
      planItem: selectedPlanItem.value.upsell.planItem,
      billingCycle: {
        type: billingCycle,
        description:
          result.value?.plans?.groupByBillingCycle.find(
            (cycle) => cycle?.billingCycleType === billingCycle
          )?.billingCycleDescription || '',
      },
    })
  }

  const handlePurchase = async (_ev?: unknown, retry = 0) => {
    if (!selectedPlanItem.value) return

    let paymentMethodId = ''
    loading.value = true
    errorMessage.value = ''

    if (!paymentMethod.value?.last4 && stripe.value && card.value) {
      const result = await stripe.value.createPaymentMethod({
        type: 'card',
        card: card.value,
      })

      if (result.error) {
        errorMessage.value = result.error.message || 'Something went wrong.'
        loading.value = false
        return
      }
      paymentMethodId = result.paymentMethod.id
    }

    try {
      const oldPlan = billingStore.billing?.plan

      const response = await createSubscription(
        {
          platform: PlatformType.Web,
          productId: selectedPlanItem.value.productId,
          ...(paymentMethodId && { paymentMethodId }),
        },
        {
          update(cache, { data }) {
            cache.updateQuery<BillingQueryQuery>(
              { query: BillingQueryDocument },
              (response) => {
                if (!response || !data) {
                  refetch()
                  return null
                }
                return {
                  ...response,
                  billing: {
                    ...response.billing,
                    plan: data.createSubscription.plan,
                    upcomingBilling:
                      data.createSubscription.plan.details.upcomingBilling,
                  },
                }
              }
            )

            cache.updateQuery<MeQuery>({ query: MeDocument }, (response) => {
              if (!response) return
              return {
                ...response,
                me: {
                  ...response.me,
                  totalFilesLimit: selectedPlanItem.value?.maxUnits,
                  permissions: response.me.permissions.concat(
                    UserPermission.Pro
                  ),
                  upcomingBilling:
                    data?.createSubscription.plan.details.upcomingBilling,
                  hasUploadedFileAsAPro:
                    userStore.user?.hasUploadedFileAsAPro || false,
                  canChangePlan: true,
                },
              }
            })
          },
        }
      )

      if (route.path.includes('onboarding')) {
        completeOnboarding()
        analytics.track('onboarding_completed', {
          trigger: 'checkout',
          planName: selectedPlan.value?.name,
          source: webviewStore.isActive
            ? webviewStore.isiOS
              ? 'apple'
              : 'google'
            : 'web',
        })
      }

      const getModalContent = () => {
        if (
          oldPlan?.type !== PlanType.Pro &&
          response?.data?.createSubscription.plan.type === PlanType.Pro
        ) {
          return 'You\u2019ve successfully changed your plan to Dacr Pro Lifetime.'
        }
        if (
          oldPlan?.type !== PlanType.Bundle &&
          response?.data?.createSubscription.plan.type === PlanType.Bundle
        ) {
          return 'You\u2019ve successfully changed your plan to Dacr Pro Bundle.'
        }
        if (
          oldPlan?.type === PlanType.Bundle &&
          response?.data?.createSubscription.plan.type === PlanType.Bundle
        ) {
          return `You\u2019ve successfully upgraded your plan limit to ${selectedPlanItem.value?.maxUnits} copyrights.`
        }
        return 'You\u2019ve successfully changed your billing period.'
      }

      if (!oldPlan || oldPlan?.type === PlanType.Basic) {
        dialog.open(Dialogs.WelcomeToDacrPro)
      } else {
        dialog.open(Dialogs.Success, {
          props: { title: 'Plan changed', html: getModalContent() },
          events: {
            success() {
              dialog.close()
            },
          },
        })
      }
    } catch (e) {
      console.error(e)
      if (retry < 2) {
        await handlePurchase(_ev, retry + 1)
      } else {
        errorMessage.value = 'Something went wrong'
      }
    } finally {
      loading.value = false
    }

    if (!errorMessage.value) {
      await router.push('/home')
    }
  }

  return {
    acceptTerms,
    loading,
    errorMessage,
    selectedPlan,
    selectedPlanItem,
    selectedBillingCycle,
    getBillingCyclePrice,
    hasMoreThanOnePrice,
    cardClasses,
    paymentMethod,
    handleChangeClick,
    formatUpsellText,
    handleUpdatePaymentMethod,
    handleUpgradeClick,
    handlePurchase,
  }
}
